import type { TextAnimationMetadata } from "./keyframeHelpers";
import {
  collapseAnimatedPropertiesToStaticValue,
  getValueAtObjectPath,
} from "./rootAnimationModHelpers";
import {
  getKeyframePropertyPath,
  type KeyframeInfo,
  type KeyframePropertyType,
} from "./keyframeHelpers";

type DisconnectAnimationChange = {
  path: string;
  value: unknown;
  closeEditor: boolean;
};

export function getMotionPropertyForRow(
  propertyType: KeyframePropertyType,
  propertyPaths: string[]
): string | null {
  if (propertyType === "opacity") return "opacity";
  if (propertyType === "rotation") return "rotation";
  if (propertyType === "scale") return "scale";
  if (propertyType === "anchor") return "anchor";

  if (propertyType === "position") {
    if (propertyPaths.length === 1) {
      if (propertyPaths[0] === "ks.p.x") return "positionX";
      if (propertyPaths[0] === "ks.p.y") return "positionY";
    }

    return "position";
  }

  return null;
}

export function getMotionRootPath(
  motionProperty: string,
  propertyPaths: string[]
): string {
  if (motionProperty === "opacity") return "ks.o";
  if (motionProperty === "rotation") return "ks.r";
  if (motionProperty === "scale") return "ks.s";
  if (motionProperty === "anchor") return "ks.a";
  if (motionProperty === "position") return "ks.p";
  if (motionProperty === "positionX") return "ks.p.x";
  if (motionProperty === "positionY") return "ks.p.y";
  return propertyPaths[0] ?? motionProperty;
}

export function buildDisabledTextAnimatorValue(
  value: unknown
): Record<string, unknown> {
  const nextValue =
    value && typeof value === "object"
      ? (JSON.parse(JSON.stringify(value)) as Record<string, unknown>)
      : {};
  const selector =
    nextValue.s && typeof nextValue.s === "object"
      ? { ...(nextValue.s as Record<string, unknown>) }
      : {};

  selector.a = { a: 0, k: 0 };
  nextValue.s = selector;

  return nextValue;
}

export function buildDisconnectAnimationChange(args: {
  keyframes: KeyframeInfo[];
  propertyType: KeyframePropertyType;
  textAnimation?: TextAnimationMetadata;
  resolvedLayerData: unknown;
  layerTiming: {
    ip: number;
    op: number;
    st?: number;
    timelineOffset?: number;
  };
  currentFrame: number;
}): DisconnectAnimationChange | null {
  const {
    keyframes,
    propertyType,
    textAnimation,
    resolvedLayerData,
    layerTiming,
    currentFrame,
  } = args;

  if (textAnimation) {
    const currentAnimator = getValueAtObjectPath(
      resolvedLayerData,
      textAnimation.rootPath
    );

    return {
      path: textAnimation.rootPath,
      value: buildDisabledTextAnimatorValue(currentAnimator),
      closeEditor: true,
    };
  }

  const propertyPaths = Array.from(
    new Set(keyframes.map((keyframe) => getKeyframePropertyPath(keyframe.path)))
  ).sort();
  const motionProperty = getMotionPropertyForRow(propertyType, propertyPaths);
  if (!motionProperty && propertyPaths.length !== 1) {
    return null;
  }

  const rootPath = motionProperty
    ? getMotionRootPath(motionProperty, propertyPaths)
    : propertyPaths[0];
  const currentRootValue = getValueAtObjectPath(resolvedLayerData, rootPath);
  const boundedCurrentFrame = Math.max(
    layerTiming.ip,
    Math.min(layerTiming.op, currentFrame)
  );
  const localCurrentFrame =
    boundedCurrentFrame - (layerTiming.timelineOffset ?? layerTiming.st ?? 0);

  return {
    path: rootPath,
    value: collapseAnimatedPropertiesToStaticValue(
      currentRootValue,
      localCurrentFrame
    ),
    closeEditor: false,
  };
}
